import { useContext, useEffect, useState } from "react";
import CacheApiContext from "../context/cache-api-context";
import axios from "../axios";
import { ApiCallType, ServerResponse } from "../types/General";
import { ZodError } from "zod";

function useApiCall<T>({url, key, fallbackData, schemaValidator, deep, invalidateCache, skipValidationType, refreshSingal,
      avoidCancelPreviosRequest, customDataKey, headers = {}, silentUpdate, onError, onSuccess}: ApiCallType) {

    const cacheApiCallContext = useContext(CacheApiContext);

    const [data, setData] = useState<unknown | null>(fallbackData);
    const [loading, setLoading] = useState(false);

  function resetData() {
    setData(null);
  }

  function setDataManually(data: any) {
    key && cacheApiCallContext?.setCache(key, data);
    setData(data);
  }

  useEffect(() => {
    if (!schemaValidator || !url || !key) return;

    const controller = new AbortController();

    if (!silentUpdate) {
      setLoading(true);
      setData(fallbackData);
    }

    if (cacheApiCallContext?.getCache(key) && !invalidateCache) {
        setData(cacheApiCallContext.getCache(key))
        setLoading(false)
    } else {
        axios.get(url, { signal: controller.signal, headers: headers })
            .then((res) => {
                if (skipValidationType) {
                    const unprocessedData = (res as ServerResponse).data as T; 
                    setData(unprocessedData);
                    onSuccess?.(unprocessedData as T);
                    cacheApiCallContext?.setCache(key, (res as ServerResponse)?.data || fallbackData);
                } else {
                    let parsedData = null;
                    if (deep)
                        parsedData = schemaValidator.parse((((res as ServerResponse).data) as ServerResponse)[customDataKey ? customDataKey as keyof ServerResponse : 'data']); 
                    else
                        parsedData = schemaValidator.parse((res as ServerResponse).data); 
                    
                    setData(parsedData);
                    onSuccess?.(parsedData)
                    cacheApiCallContext?.setCache(key, parsedData);
                }
            })
            .catch((e) => {
                console.log(e)
                if (!silentUpdate) {
                  onError?.();
                  setData(fallbackData);
                }
                if (e instanceof ZodError)
                    cacheApiCallContext?.setCache(key, fallbackData);
            })
            .finally(() => {
                setLoading(false)
            })
    }

    return () => {
      if (!avoidCancelPreviosRequest) {
        controller.abort();
      }
    };
  }, [
    url,
    key,
    fallbackData,
    schemaValidator,
    invalidateCache,
    skipValidationType,
    refreshSingal,
  ]);

  return {
    data: data as T,
    loading,
    resetData,
    setLoading,
    setDataManually,
  };
}

export default useApiCall;
